import { jsxs, jsx } from 'react/jsx-runtime';
import { useBoolState } from '@fabric-app/hooks/useBooleanState';
import { useInputControls } from '@fabric-app/hooks/useInputControls';
import { useWindowEvent } from '@fabric-app/hooks/useWindowEvent';
import Color from 'color';
import { isHotkey } from 'is-hotkey';
import { useState } from 'react';
import { HexColorPicker as Z } from '../../node_modules/react-colorful/dist/index.js';
import { styled, css } from 'styled-components';
import { DropdownMenu } from './DropdownMenu/DropdownMenu.js';
import { DrawerDragHandle, DrawerTitle } from './Modal/Drawer.js';
import { Flex } from './Flex.js';
import { mediaMobile } from '../constants/styled-utils.js';

const ColorPicker = ({ buttonElement, ...props }) => {
  const [color, setColor] = useState(props.value);
  const inputControls = useInputControls(props.value ? props.value.replace("#", "") : "", {
    onChange: (value) => {
      try {
        if (!value) {
          return;
        }
        const color2 = new Color("#" + value);
        const hex = color2.hex().toString();
        setColor(hex);
        props.onChange(hex);
      } catch {
      }
    }
  });
  const handlePickerSetColor = (color2) => {
    setColor(color2);
    inputControls.onChange(color2?.replace("#", "") || "");
    props.onChange(color2);
  };
  const { modalProps } = useBoolState(false);
  useWindowEvent("keydown", (e) => {
    if (isHotkey("enter", e)) {
      e.preventDefault();
      modalProps.onOpenChange(false);
    }
  }, {
    enabled: modalProps.open
  });
  return /* @__PURE__ */ jsxs(DropdownMenu.Base, {
    openProps: modalProps,
    renderTriggerElement: (p) => /* @__PURE__ */ jsx(DropdownMenu.Trigger, {
      asChild: true,
      ...p,
      children: buttonElement
    }),
    contentProps: {
      side: "bottom",
      align: "center",
      style: {
        borderRadius: 18,
        border: "none",
        backgroundColor: "#000"
      }
    },
    children: [
      /* @__PURE__ */ jsx(DrawerDragHandle, {}),
      /* @__PURE__ */ jsx(DropdownMenu.GroupTitle, {
        children: /* @__PURE__ */ jsx(DrawerTitle, {
          style: {
            paddingBottom: "1rem"
          },
          children: "Search by color"
        })
      }),
      /* @__PURE__ */ jsx(Container, {
        isColorPicked: !!props.value,
        children: /* @__PURE__ */ jsxs(InnerContainer, {
          "data-pan-id-ignore-dropdown-content": "y",
          children: [
            /* @__PURE__ */ jsx(Z, {
              color,
              onChange: handlePickerSetColor
            }),
            /* @__PURE__ */ jsxs(InputContainer, {
              children: [
                /* @__PURE__ */ jsx(Hash, {
                  children: "#"
                }),
                /* @__PURE__ */ jsx(Input, {
                  placeholder: "Hex code",
                  value: inputControls.value,
                  onChange: inputControls.onChange
                })
              ]
            })
          ]
        })
      })
    ]
  });
};
const Container = styled(Flex).attrs({
  direction: "column",
  gap: 12
}).withConfig({
  displayName: "ColorPicker__Container",
  componentId: "sc-838c7441-0"
})([
  `padding:0.5rem;width:9rem;`,
  `{width:100%;padding:0 2rem;gap:20px;}& .react-colorful{width:100%;height:auto;gap:0.75rem;`,
  `{gap:20px;}}& .react-colorful__saturation{border-radius:6px;height:6rem;flex-grow:0;border-bottom:none;`,
  `{height:150px;border-radius:1rem;}}& .react-colorful__saturation-pointer{`,
  `}& .react-colorful__hue{height:0.75rem;flex-grow:0;border-radius:6px;`,
  `{height:1.5rem;border-radius:0.75rem;}}& .react-colorful__pointer{width:18px;height:18px;border:3px solid white;.react-colorful__pointer-fill{width:0.75rem;height:0.75rem;}`,
  `{width:2.25rem;height:2.25rem;border:6px solid white;.react-colorful__pointer-fill{width:1.5rem;height:1.5rem;}box-shadow:0px 6.33px 12.67px 0px #00000040;}}`
], mediaMobile, mediaMobile, mediaMobile, (p) => !p.isColorPicked && css([
  `display:none;`
]), mediaMobile, mediaMobile);
const Input = styled.input.withConfig({
  displayName: "ColorPicker__Input",
  componentId: "sc-838c7441-1"
})([
  `background:#f3f3f3;color:rgba(0,0,0,0.5);border-radius:0.5rem;padding:0 0.5rem 0 21px;height:28px;width:100%;min-width:1px;outline:none;text-transform:uppercase;`,
  `{font-size:15px;height:44px;padding-left:2rem;font-weight:500;border-radius:1rem;}`
], mediaMobile);
const InputContainer = styled.div.withConfig({
  displayName: "ColorPicker__InputContainer",
  componentId: "sc-838c7441-2"
})([
  `position:relative;width:100%;`
]);
const Hash = styled.span.withConfig({
  displayName: "ColorPicker__Hash",
  componentId: "sc-838c7441-3"
})([
  `position:absolute;left:8px;top:50%;font-size:0.875rem;transform:translateY(-50%);color:rgba(0,0,0,0.5);pointer-events:none;display:flex;align-items:center;justify-content:center;`,
  `{left:16px;font-size:15px;height:44px;font-weight:500;}`
], mediaMobile);
const InnerContainer = styled.div.withConfig({
  displayName: "ColorPicker__InnerContainer",
  componentId: "sc-838c7441-4"
})([
  `display:flex;flex-direction:column;gap:12px;`,
  `{gap:20px;padding-top:2px;}`
], mediaMobile);

export { ColorPicker };
//# sourceMappingURL=ColorPicker.js.map
